import React from "react";
import { Link } from "react-router-dom";
import { LOGO_R_HEADER } from "@/lib/brand";

const MB_EXTERNAL_URL = "https://maisonbeaudette.com";

export function Header({ variant = "ivory" }) {
  const isDark = variant === "dark";
  return (
    <header className={`relative z-20 ${isDark ? "bg-ink" : "bg-ivory"}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-4 md:py-5 flex items-center justify-between gap-4">
        {/* À gauche : emblème + Maison Beaudette™ + Le Révélateur™ */}
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-home-link">
          <img src={LOGO_R_HEADER} alt="" className="h-9 w-9 md:h-10 md:w-10 object-contain" />
          <div className="leading-tight">
            <div className={`text-[9px] md:text-[10px] tracking-[0.28em] md:tracking-[0.32em] uppercase ${isDark ? "text-bronze-light" : "text-bronze"}`}>Maison Beaudette<span className="text-[7px] md:text-[8px] align-top">™</span></div>
            <div className={`font-display text-base md:text-lg ${isDark ? "text-ivory" : "text-ink"}`}>Le Révélateur<span className="text-[8px] md:text-[9px] align-top">™</span></div>
          </div>
        </Link>

        {/* À droite : lien discret de récupération + CTA principal */}
        <div className="flex items-center gap-4 md:gap-6">
          <Link
            to="/retrouver"
            className={`hidden sm:inline text-[10px] md:text-[11px] tracking-[0.26em] uppercase ${isDark ? "text-ivory-2/70 hover:text-bronze-light" : "text-text-2/80 hover:text-bronze"}`}
            data-testid="nav-retrieve"
          >
            Retrouver ma lecture
          </Link>
          <Link
            to="/commencer"
            className={isDark ? "btn-primary-on-dark" : "btn-primary"}
            data-testid="nav-start"
          >
            Commencer — 49 $
          </Link>
        </div>
      </div>
      <div className="architect-line" />
    </header>
  );
}

export function Footer() {
  return (
    <footer className="mt-24 bg-grad-mix" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-4 gap-10">
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-bronze-light">Maison Beaudette<span className="text-[8px] align-top">™</span></div>
          <div className="font-display text-2xl text-ivory mt-2">Le Révélateur<span className="text-[10px] align-top">™</span></div>
          <p className="text-ivory-2 mt-4 text-sm leading-relaxed max-w-xs">Un instrument propriétaire de lecture stratégique développé par Maison Beaudette™.</p>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-bronze-light mb-4">Instrument</div>
          <ul className="space-y-2 text-sm text-ivory-2">
            <li><Link to="/methode" className="hover:text-bronze-light" data-testid="footer-method">La Méthode MB™</Link></li>
            <li><Link to="/commencer" className="hover:text-bronze-light" data-testid="footer-start">Commencer une lecture</Link></li>
            <li><Link to="/retrouver" className="hover:text-bronze-light" data-testid="footer-retrieve">Retrouver ma lecture</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-bronze-light mb-4">Documents</div>
          <ul className="space-y-2 text-sm text-ivory-2">
            <li><Link to="/politique-de-confidentialite" className="hover:text-bronze-light" data-testid="footer-privacy">Politique de confidentialité</Link></li>
            <li><Link to="/conditions-de-vente" className="hover:text-bronze-light" data-testid="footer-sales">Conditions de vente</Link></li>
            <li><Link to="/conditions-utilisation" className="hover:text-bronze-light" data-testid="footer-terms">Conditions d'utilisation</Link></li>
            <li><Link to="/politique-des-temoins" className="hover:text-bronze-light" data-testid="footer-cookies">Politique des témoins</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.32em] uppercase text-bronze-light mb-4">Maison</div>
          <ul className="space-y-2 text-sm text-ivory-2">
            <li>
              <a
                href={MB_EXTERNAL_URL}
                className="hover:text-bronze-light"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-mb-external"
              >Découvrir Maison Beaudette™</a>
            </li>
            <li><Link to="/contact" className="hover:text-bronze-light" data-testid="footer-contact">Soutien</Link></li>
            <li><Link to="/contact" className="hover:text-bronze-light" data-testid="footer-contact-2">Nous joindre</Link></li>
            <li><span className="text-ivory-2/60">Drummondville, Québec, Canada</span></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between text-[11px] tracking-[0.26em] uppercase text-ivory-2/70">
          <div>© {new Date().getFullYear()} Maison Beaudette™</div>
          <div>Observer · Interpréter · Révéler · Orienter</div>
        </div>
      </div>
    </footer>
  );
}

export function Page({ children, tone = "ivory" }) {
  const bg = tone === "dark" ? "bg-ink" : tone === "stone" ? "bg-stone" : "bg-ivory";
  return <div className={`min-h-screen relative ${bg}`}>{children}</div>;
}

export { MB_EXTERNAL_URL };
